import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [showForm, setShowForm] = useState(false);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 欧美无码正版平台推荐指南',
    description: 'UltraGuide 品牌介绍、信誉展示与联系方式，专业的欧美无码平台评测团队。',
    url: 'https://massdreamer.cn/about',
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'UltraGuide',
    url: 'https://massdreamer.cn/',
    description: '专业的欧美无码正版内容平台评测与推荐团队，致力于为用户提供客观、全面的平台评测信息。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@ultraguide.com',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://massdreamer.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const teamMembers = [
    {
      name: 'Alex Chen',
      role: '创始人 & 主编',
      bio: '拥有10年数字内容行业经验，曾任多家知名流媒体平台内容顾问。致力于为用户提供最客观的欧美无码平台评测。',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20male%20portrait%20dark%20background%20sophisticated%20editorial%20photography%20warm%20lighting%20elegant%20minimalist&width=200&height=200&seq=team-alex&orientation=squarish',
    },
    {
      name: 'Sarah Wang',
      role: '技术评测总监',
      bio: '资深技术评测专家，专注4K/8K画质分析、VR技术评测和流媒体性能测试。拥有计算机科学硕士学位。',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20female%20portrait%20dark%20background%20sophisticated%20editorial%20photography%20warm%20lighting%20elegant%20minimalist&width=200&height=200&seq=team-sarah&orientation=squarish',
    },
    {
      name: 'Mike Liu',
      role: '安全顾问',
      bio: '网络安全专家，持有CISSP认证。为用户提供隐私保护和安全访问欧美无码平台的专业建议。',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20male%20portrait%20dark%20background%20sophisticated%20editorial%20photography%20warm%20lighting%20elegant%20minimalist&width=200&height=200&seq=team-mike&orientation=squarish',
    },
    {
      name: 'Emily Zhang',
      role: '内容编辑',
      bio: '资深内容创作者，擅长将复杂的平台评测转化为通俗易懂的选购指南。拥有新闻传播学背景。',
      avatar: 'https://readdy.ai/api/search-image?query=Professional%20female%20portrait%20dark%20background%20sophisticated%20editorial%20photography%20warm%20lighting%20elegant%20minimalist&width=200&height=200&seq=team-emily&orientation=squarish',
    },
  ];

  const milestones = [
    { year: '2024', title: '平台创立', desc: 'UltraGuide正式上线，开始为成年用户提供欧美无码平台评测服务' },
    { year: '2025', title: '评测体系建立', desc: '建立五大维度评测体系，覆盖内容质量、用户体验、性价比、更新速度和安全性' },
    { year: '2025', title: '用户突破10万', desc: '月活跃用户突破10万，成为中文互联网领域领先的欧美无码平台推荐指南' },
    { year: '2026', title: 'VR评测拓展', desc: '拓展VR成人内容评测领域，与主流VR设备厂商建立合作关系' },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 欧美无码正版内容平台推荐指南"
        description="UltraGuide 品牌介绍、信誉展示与联系方式。专业的欧美无码平台评测团队，致力于为成年用户提供最客观、全面的平台推荐与评测信息。"
        keywords="欧美无码,欧美无码平台推荐,欧美无码评测团队,关于我们"
        canonicalUrl="https://massdreamer.cn/about"
        schema={{ ...aboutSchema, ...orgSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 px-4 md:px-6 pb-16">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="mt-8 mb-16">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-800">
                关于 UltraGuide
              </h1>
              <p className="mt-4 text-base text-foreground-500 leading-relaxed max-w-3xl">
                UltraGuide 是专业的欧美无码正版内容平台推荐指南。我们致力于为成年用户提供最全面、最客观的平台评测信息，
                帮助用户在海量的欧美无码平台中找到最适合自己的选择。
              </p>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-bold text-foreground-800 mb-8">
                我们的使命
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: 'ri-scales-3-line', title: '客观评测', desc: '基于五大维度评分体系，独立客观地评测每一个欧美无码平台，不受商业利益影响。' },
                  { icon: 'ri-shield-star-line', title: '安全保障', desc: '为用户提供全面的隐私保护和安全访问指南，确保每位用户的个人信息和支付安全。' },
                  { icon: 'ri-guide-line', title: '专业指导', desc: '从平台选择到使用技巧，为用户提供全程专业指导，让新手也能快速上手。' },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="bg-background-100 rounded-xl p-6 border border-background-200/50"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mb-4">
                      <i className={`${item.icon} text-primary-500 text-xl`}></i>
                    </div>
                    <h3 className="text-lg font-heading font-semibold text-foreground-800 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-bold text-foreground-800 mb-8">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-background-200"></div>
                <div className="flex flex-col gap-8">
                  {milestones.map((item, index) => (
                    <div key={index} className="flex gap-6 items-start">
                      <div className="relative z-10 w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-background-950">{item.year.slice(2)}</span>
                      </div>
                      <div className="flex-1 bg-background-100 rounded-xl p-5 border border-background-200/50">
                        <span className="text-xs text-primary-500 font-semibold">{item.year}</span>
                        <h3 className="text-base font-heading font-semibold text-foreground-800 mt-1">{item.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-bold text-foreground-800 mb-8">
                核心团队
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {teamMembers.map((member) => (
                  <div
                    key={member.name}
                    className="bg-background-100 rounded-xl p-5 border border-background-200/50 text-center"
                  >
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-20 h-20 rounded-full mx-auto object-cover"
                    />
                    <h3 className="mt-4 text-base font-heading font-semibold text-foreground-800">
                      {member.name}
                    </h3>
                    <span className="text-xs text-primary-500">{member.role}</span>
                    <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{member.bio}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-bold text-foreground-800 mb-8">
                评测标准
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {[
                  { label: '内容质量', weight: '30%', desc: '评估平台的画质、音效、内容多样性及独家内容比例' },
                  { label: '用户体验', weight: '25%', desc: '考察界面设计、导航便捷性、加载速度和设备兼容性' },
                  { label: '性价比', weight: '20%', desc: '综合价格、功能丰富度和订阅灵活性进行评估' },
                  { label: '更新速度', weight: '15%', desc: '评测新内容上线频率、独家内容更新和内容时效性' },
                  { label: '安全性', weight: '10%', desc: '评估隐私保护措施、支付安全和平台合规性' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-start gap-4 bg-background-100 rounded-xl p-5 border border-background-200/50"
                  >
                    <span className="text-2xl font-heading font-bold text-primary-500 flex-shrink-0">
                      {item.weight}
                    </span>
                    <div>
                      <h3 className="text-base font-heading font-semibold text-foreground-800">
                        {item.label}
                      </h3>
                      <p className="text-sm text-foreground-500 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-16">
              <h2 className="text-2xl font-heading font-bold text-foreground-800 mb-8">
                免责声明
              </h2>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/50">
                <div className="space-y-4 text-sm text-foreground-500 leading-relaxed">
                  <p>
                    <strong className="text-foreground-700">年龄限制：</strong>
                    本网站内容仅供18岁以上成年人访问。如果您未满18岁，请立即离开本网站。
                  </p>
                  <p>
                    <strong className="text-foreground-700">信息准确性：</strong>
                    本网站提供的平台评测和推荐信息基于团队的专业分析和用户反馈，仅供参考。平台的价格、功能和内容可能会随时变化，请以各平台官网信息为准。
                  </p>
                  <p>
                    <strong className="text-foreground-700">法律合规：</strong>
                    请遵守您所在地区的法律法规。本网站不对用户使用推荐平台时可能产生的法律问题承担责任。
                  </p>
                  <p>
                    <strong className="text-foreground-700">外部链接：</strong>
                    本网站包含指向第三方平台的链接，我们对这些平台的内容和隐私政策不承担责任。
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-heading font-bold text-foreground-800 mb-8">
                联系我们
              </h2>
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/50">
                <p className="text-sm text-foreground-500 mb-6">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式联系我们：
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-700">电子邮件</h4>
                      <p className="text-xs text-foreground-500 mt-1">contact@ultraguide.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-twitter-x-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-700">社交媒体</h4>
                      <p className="text-xs text-foreground-500 mt-1">@UltraGuide_Official</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-500"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-700">响应时间</h4>
                      <p className="text-xs text-foreground-500 mt-1">通常24小时内回复</p>
                    </div>
                  </div>
                </div>

                {!showForm ? (
                  <button
                    onClick={() => setShowForm(true)}
                    className="px-6 py-3 bg-primary-500 text-background-950 font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
                  >
                    发送消息
                    <i className="ri-send-plane-line ml-2"></i>
                  </button>
                ) : (
                  <form
                    action="https://readdy.ai/api/form/d9sq2qt8o1vn02ekuk4g"
                    method="POST"
                    encType="application/x-www-form-urlencoded"
                    className="space-y-4"
                    data-readdy-form=""
                    id="contact-form-about"
                    onSubmit={(e) => {
                      const form = e.currentTarget;
                      const honeypot = form.querySelector('input[name="phone_alt"]') as HTMLInputElement;
                      if (honeypot && honeypot.value.trim() !== '') {
                        e.preventDefault();
                        alert('消息已发送，感谢您的留言！');
                        return;
                      }
                      setTimeout(() => {
                        alert('消息已发送，感谢您的留言！');
                        form.reset();
                      }, 500);
                    }}
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-600 mb-1">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-600 mb-1">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 focus:outline-none focus:border-primary-500 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-600 mb-1">留言内容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="px-6 py-3 bg-primary-500 text-background-950 font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
                      >
                        提交留言
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="px-6 py-3 border border-background-300 text-foreground-500 rounded-lg hover:bg-background-200/50 transition-all duration-300 cursor-pointer text-sm whitespace-nowrap"
                      >
                        取消
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}